import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ImageIcon from "@mui/icons-material/Image";
import Swal from "sweetalert2";
import { createEvidencia, updateEvidencia } from "../services/evidenciasService";
import { API_BASE_URL } from "../api/constants";

const getEvidenceId = (ev) =>
  ev?.idevidencias ?? ev?.id ?? ev?.Id ?? ev?.ID ?? ev?.idEvidencia ?? ev?.IdEvidencia ?? ev?.Id_Evidencia;

/** Convierte el valor Imagen de la API en una URL válida para <img src>. */
const getImagePreviewSrc = (imagen) => {
  if (!imagen || typeof imagen !== "string") return "";
  const s = imagen.trim().replace(/\s/g, "");
  if (!s) return "";
  if (s.startsWith("data:")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (/^[A-Za-z0-9+/=]+$/.test(s)) return `data:image/jpeg;base64,${s}`;
  return `${API_BASE_URL.replace(/\/$/, "")}/${s.replace(/^\//, "")}`;
};

const initialFormState = {
  Seccion: "",
  Titular: "",
  SubTitulo: "",
  Cuerpo: "",
  Fuente: "",
  Imagen: "",
  Fecha: new Date().toISOString().slice(0, 10),
  Estado: 1,
};

/**
 * Formulario único para agregar o editar una evidencia.
 * @param {Object} evidenciaData - Si se pasa, modo edición; si no, modo alta.
 * @param {Function} onSuccess - Callback al guardar correctamente (ej: navegar atrás).
 */
const EvidenceForm = ({ evidenciaData, onSuccess }) => {
  const isEdit = Boolean(evidenciaData);
  const [formData, setFormData] = useState(initialFormState);
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (evidenciaData) {
      const d = evidenciaData;
      const imagen = d.Imagen ?? d.imagen ?? "";
      setFormData({
        Seccion: d.Seccion ?? "",
        Titular: d.Titular ?? "",
        SubTitulo: d.SubTitulo ?? "",
        Cuerpo: d.Cuerpo ?? "",
        Fuente: d.Fuente ?? "",
        Imagen: imagen,
        Fecha: d.Fecha?.slice?.(0, 10) ?? new Date().toISOString().slice(0, 10),
        Estado: Number(d.Estado) === 0 ? 0 : 1,
      });
      setPreview(getImagePreviewSrc(imagen));
      setFileName("");
    } else {
      setFormData(initialFormState);
      setPreview("");
      setFileName("");
    }
  }, [evidenciaData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === "Estado" ? parseInt(value, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, Imagen: reader.result }));
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!formData.Titular?.trim()) {
      return Swal.fire("Error", "El título de la evidencia es obligatorio", "error");
    }
    if (!formData.Cuerpo?.trim()) {
      return Swal.fire("Error", "El contenido o descripción es obligatorio", "error");
    }

    const payload = {
      Seccion: formData.Seccion?.trim() || "",
      Titular: formData.Titular.trim(),
      SubTitulo: formData.SubTitulo?.trim() || "",
      Cuerpo: formData.Cuerpo.trim(),
      Fuente: formData.Fuente?.trim() || "",
      Imagen: formData.Imagen || "",
      Fecha: formData.Fecha || new Date().toISOString().slice(0, 10),
      Estado: formData.Estado,
    };

    try {
      setLoading(true);

      if (isEdit) {
        const id = getEvidenceId(evidenciaData);
        if (id == null) {
          Swal.fire("Error", "No se puede identificar la evidencia.", "error");
          return;
        }
        const response = await updateEvidencia(id, payload);
        if (response?.success !== false) {
          Swal.fire({ icon: "success", title: "Evidencia actualizada", text: "Los cambios se guardaron correctamente." });
          onSuccess?.();
        } else {
          Swal.fire("Error", response?.message || "Error al actualizar la evidencia", "error");
        }
      } else {
        const response = await createEvidencia(payload);
        if (response?.success !== false) {
          Swal.fire({ icon: "success", title: "Evidencia creada", text: "La evidencia se ha registrado correctamente." });
          setFormData(initialFormState);
          setPreview("");
          setFileName("");
          onSuccess?.();
        } else {
          Swal.fire("Error", response?.message || "Error al crear la evidencia", "error");
        }
      }
    } catch (error) {
      console.error(isEdit ? "Error al actualizar evidencia:" : "Error al crear evidencia:", error);
      const message = error?.response?.data?.message || error?.message || "Error en la conexión con el servidor";
      Swal.fire("Error", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(initialFormState);
    setPreview("");
    setFileName("");
  };

  return (
    <Card sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "none" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <FactCheckIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            {isEdit ? "Editar evidencia" : "Nueva evidencia"}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sección"
                name="Seccion"
                value={formData.Seccion}
                onChange={handleChange}
                placeholder="Ej: Contaminación"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha"
                name="Fecha"
                type="date"
                value={formData.Fecha}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título de la evidencia"
                name="Titular"
                value={formData.Titular}
                onChange={handleChange}
                required
                placeholder="Título de la evidencia"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtítulo (opcional)"
                name="SubTitulo"
                value={formData.SubTitulo}
                onChange={handleChange}
                placeholder="Subtítulo opcional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contenido o descripción"
                name="Cuerpo"
                value={formData.Cuerpo}
                onChange={handleChange}
                required
                multiline
                rows={4}
                placeholder="Contenido o descripción de la evidencia"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fuente (URL)"
                name="Fuente"
                value={formData.Fuente}
                onChange={handleChange}
                placeholder="https://ejemplo.com"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: "1px dashed #ccc",
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  minHeight: 200,
                }}
              >
                {preview ? (
                  <Box sx={{ width: "100%" }}>
                    <Box
                      component="img"
                      src={preview}
                      alt="Vista previa"
                      sx={{
                        display: "block",
                        width: "100%",
                        maxWidth: 400,
                        height: "auto",
                        minHeight: 120,
                        maxHeight: 280,
                        objectFit: "contain",
                        borderRadius: 1,
                        mb: 1,
                        mx: "auto",
                      }}
                    />
                    <Typography variant="caption" display="block">{fileName || (isEdit ? "Imagen actual" : "")}</Typography>
                    <Button
                      size="small"
                      onClick={() => { setPreview(""); setFileName(""); setFormData((prev) => ({ ...prev, Imagen: "" })); }}
                      sx={{ mt: 1 }}
                    >
                      Quitar imagen
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: 48, color: "action.disabled", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>Imagen (opcional)</Typography>
                    <input
                      accept="image/*"
                      id="evidence-form-image"
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleImageChange}
                    />
                    <label htmlFor="evidence-form-image">
                      <Button variant="outlined" component="span">Seleccionar imagen</Button>
                    </label>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="estado-evidence-label">Estado</InputLabel>
                <Select
                  labelId="estado-evidence-label"
                  name="Estado"
                  value={formData.Estado}
                  label="Estado"
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Activo</MenuItem>
                  <MenuItem value={0}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                {!isEdit && (
                  <Button variant="outlined" onClick={handleClear} type="button">
                    Limpiar
                  </Button>
                )}
                <Button variant="contained" color="primary" type="submit" disabled={loading}>
                  {loading ? "Guardando..." : isEdit ? "Guardar cambios" : "Guardar evidencia"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EvidenceForm;
